import { ThemeEvents } from '@theme/events';

const ROTATION_INTERVAL_MS = 3000;
const MESSAGE_FADE_MS = 300;

/** @type {WeakMap<HTMLElement, { intervalId: number; index: number; messages: string[] }>} */
const rotationState = new WeakMap();

/**
 * @param {number} cents
 * @returns {string}
 */
function formatMoneyFromCents(cents) {
  const amount = cents / 100;
  const locale = document.documentElement.lang || 'pt-PT';
  const currency =
    typeof Shopify !== 'undefined' && Shopify.currency?.active ? Shopify.currency.active : 'EUR';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

/**
 * @param {HTMLElement} root
 * @param {{ total_price: number; items: Array<{ quantity: number }> }} cart
 * @returns {string[]}
 */
function getRotationMessages(root, cart) {
  const threshold = Number(root.dataset.shippingThreshold) || 5000;
  const minItems = Number(root.dataset.discountMinItems) || 2;

  let glassesCount = 0;
  for (const item of cart.items) {
    glassesCount += item.quantity;
  }

  const shippingReached = cart.total_price >= threshold;
  const discountReached = glassesCount >= minItems;

  if (shippingReached && discountReached) {
    return [root.dataset.msgAllUnlocked || ''];
  }

  /** @type {string[]} */
  const messages = [];

  if (!shippingReached) {
    const shippingRemaining = Math.max(0, threshold - cart.total_price);
    const shippingTemplate = root.dataset.msgShipping || '';
    messages.push(
      shippingTemplate.replace(
        '@@AMOUNT@@',
        `<strong>${formatMoneyFromCents(shippingRemaining)}</strong>`
      )
    );
  }

  if (!discountReached) {
    const itemsNeeded = Math.max(0, minItems - glassesCount);
    if (itemsNeeded === 1) {
      messages.push(root.dataset.msgDiscountOne || '');
    } else {
      const template = root.dataset.msgDiscountOther || '';
      messages.push(template.replace('@@COUNT@@', String(itemsNeeded)));
    }
  }

  return messages;
}

/**
 * @param {HTMLElement} messageEl
 * @param {string} html
 * @param {boolean} animate
 */
function setMessageContent(messageEl, html, animate) {
  if (!animate || messageEl.innerHTML === html) {
    messageEl.innerHTML = html;
    return;
  }

  messageEl.classList.add('product-rewards-progress__message--changing');

  window.setTimeout(() => {
    messageEl.innerHTML = html;
    messageEl.classList.remove('product-rewards-progress__message--changing');
  }, MESSAGE_FADE_MS);
}

/**
 * @param {HTMLElement} root
 * @param {string[]} messages
 * @param {number} index
 * @param {boolean} animate
 */
function showRotatingMessage(root, messages, index, animate) {
  const messageEl = root.querySelector('[data-prp-message]');
  if (!(messageEl instanceof HTMLElement) || messages.length === 0) return;

  const safeIndex = index % messages.length;
  setMessageContent(messageEl, messages[safeIndex], animate);
}

/**
 * @param {HTMLElement} root
 */
function startMessageRotation(root) {
  const existing = rotationState.get(root);
  if (existing?.intervalId) {
    window.clearInterval(existing.intervalId);
  }

  const state = existing ?? { intervalId: 0, index: 0, messages: [] };
  rotationState.set(root, state);

  if (state.messages.length < 2) {
    showRotatingMessage(root, state.messages, 0, false);
    return;
  }

  showRotatingMessage(root, state.messages, state.index, false);

  state.intervalId = window.setInterval(() => {
    const current = rotationState.get(root);
    if (!current || current.messages.length < 2) return;

    current.index = (current.index + 1) % current.messages.length;
    showRotatingMessage(root, current.messages, current.index, true);
  }, ROTATION_INTERVAL_MS);
}

/**
 * @param {HTMLElement} root
 * @param {{ total_price: number; items: Array<{ quantity: number }> }} cart
 */
function updateProductRewardsProgress(root, cart) {
  const threshold = Number(root.dataset.shippingThreshold) || 5000;
  const minItems = Number(root.dataset.discountMinItems) || 2;

  let glassesCount = 0;
  for (const item of cart.items) {
    glassesCount += item.quantity;
  }

  const shippingReached = cart.total_price >= threshold;
  const discountReached = glassesCount >= minItems;

  const shippingFill = Math.min(100, Math.max(0, Math.round((cart.total_price / threshold) * 100)));
  const discountFill = Math.min(100, Math.max(0, Math.round((glassesCount / minItems) * 100)));

  const shippingFillEl = root.querySelector('[data-prp-shipping-fill]');
  const discountFillEl = root.querySelector('[data-prp-discount-fill]');

  if (shippingFillEl instanceof HTMLElement) {
    shippingFillEl.style.width = `${shippingFill}%`;
    shippingFillEl.classList.toggle('product-rewards-progress__fill--complete', shippingReached);
  }

  if (discountFillEl instanceof HTMLElement) {
    discountFillEl.style.width = `${discountFill}%`;
    discountFillEl.classList.toggle('product-rewards-progress__fill--complete', discountReached);
  }

  const labels = root.querySelectorAll('.product-rewards-progress__label');
  if (labels[0]) labels[0].classList.toggle('product-rewards-progress__label--reached', shippingReached);
  if (labels[1]) labels[1].classList.toggle('product-rewards-progress__label--reached', discountReached);

  const messages = getRotationMessages(root, cart);
  const state = rotationState.get(root) ?? { intervalId: 0, index: 0, messages: [] };
  const messagesChanged =
    state.messages.length !== messages.length ||
    state.messages.some((message, index) => message !== messages[index]);

  state.messages = messages;
  if (messagesChanged) {
    state.index = 0;
  }
  rotationState.set(root, state);
  startMessageRotation(root);
}

/**
 * @param {HTMLElement} root
 */
async function refreshProductRewardsProgress(root) {
  const response = await fetch('/cart.js');
  const cart = await response.json();
  updateProductRewardsProgress(root, cart);
}

function initProductRewardsProgress() {
  const roots = document.querySelectorAll('[data-product-rewards-progress]');
  for (const root of roots) {
    if (root instanceof HTMLElement) {
      refreshProductRewardsProgress(root);
    }
  }
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  initProductRewardsProgress();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductRewardsProgress);
} else {
  initProductRewardsProgress();
}
